import React, { useMemo, useState } from "react";
import styled from "styled-components";

import { Chip } from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import BillingController from "js/controllers/BillingController";
import { FlexBox } from "js/react/components/LayoutGrid";
import { Gap10 } from "js/react/components/Gap";
import { Divider } from "js/Components/Divider";
import { TAG_DELIMITERS } from "js/react/constants";
import TagsInput from "react-tagsinput";
import { delimiterRegex, emailRegex } from "common/regex";
import { themeColors } from "js/react/sharedStyles";
import { defaultText } from "js/textStyles";
import { pluralize } from "js/core/utilities/utilities";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { t } from "js/i18n";

export type SeatsInfoVariant = "default" | "invite-only";

export interface SeatsInfoContentProps {
    show?: boolean;
    variant: SeatsInfoVariant;
    availableSeatCount: number;
    additionalSeatCount: number;
    emailsCount: number;
    paidSeatCount: number;
    existingUserEmailsCount: number;
    dataTestIdPrefix?: string;
}

const StyledEmailChip = styled(({ isValid, ...props }) => <Chip {...props} />)`
    &&& {
        background: #F1F1F1;
        margin: 2px;
    
        .MuiChip-label {
            text-align: center;
            font-family: "Source Sans Pro";
            font-size: 13px;
            font-style: normal;
            font-weight: 600;
            line-height: normal;
            color: ${props => props.isValid ? themeColors.mediumGray : "#ff0000"};
        }
    }
`;

const StyledEmailTagsInput = styled(TagsInput) <{ hasError?: boolean; isFocused?: boolean }>`
    width: 100%;
    padding: 6px;
    background-color: #fff;
    border: 1px solid ${props => props.hasError ? "#ff0000" : props.isFocused ? "#11a9e2" : "rgba(0,0,0,0.23)"};
    border-radius: 4px;
    overflow: hidden;
    font-size: 16px;
    
    &:hover {
        border-color: ${props => props.hasError ? "#ff0000" : props.isFocused ? "#11a9e2" : "#333"};
    }
`;

const EmailTagLayout = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    align-items: center;
    gap: 4px;
    min-height: 40px;
`;

const EmailInput = styled.input`
    background: transparent;
    border: 0;
    color: #222;
    outline: none;
    font-size: 16px;
    min-width: 200px;
    flex: 1;
    flex-shrink: 0;
    height: 32px;
    display: flex;
    align-items: center;
`;

const SectionContent = styled.div`
    ${defaultText}
    color: ${themeColors.mediumGray};
`;

export function SeatsInfoContent({
    show = true,
    variant,
    availableSeatCount,
    additionalSeatCount,
    emailsCount,
    paidSeatCount,
    existingUserEmailsCount,
    dataTestIdPrefix,
}: SeatsInfoContentProps): React.ReactElement | null {
    if (!show) {
        return null;
    }

    const sectionContentProps = {
        "data-testid": dataTestIdPrefix ? `${dataTestIdPrefix}-seats-info-text` : undefined
    };

    if (variant === "invite-only") {
        const usedSeats = Math.min(existingUserEmailsCount + emailsCount, paidSeatCount);
        return (
            <SectionContent {...sectionContentProps}>
                {t("{{used}}/{{total}} seats used", { used: usedSeats, total: paidSeatCount })}
                {additionalSeatCount > 0 && (
                    <>
                        {" → "}
                        {t("Add {{seats}}", {
                            seats: pluralize(t("seat"), additionalSeatCount, t("seats"))
                        })}
                    </>
                )}
            </SectionContent>
        );
    }

    // default variant
    if (availableSeatCount > 0) {
        return (
            <SectionContent {...sectionContentProps}>
                {t("You have {{seats}}", {
                    seats: pluralize(t("available seat"), availableSeatCount, t("available seats"))
                })}
            </SectionContent>
        );
    }
    if (availableSeatCount <= 0 && additionalSeatCount <= 0 && emailsCount > 0) {
        return <SectionContent {...sectionContentProps}>{t("No seats available.")}</SectionContent>;
    }
    if (additionalSeatCount > 0) {
        return (
            <SectionContent {...sectionContentProps}>
                {t("You are adding {{seats}}", {
                    seats: pluralize(t("new seat"), additionalSeatCount, t("new seats"))
                })}
            </SectionContent>
        );
    }

    return null;
}

const TotalDueToday = styled.div`
    ${defaultText}
    color: #333;
    font-weight: 600;
`;

export interface InviteSectionProps {
    emails: string[];
    onEmailsChange: (emails: string[]) => void;
    nextBillDate: string;
    additionalSeatCount?: number;
    totalDueToday?: number;
    nextInvoiceTotal?: number;
    isCalculating?: boolean;
    existingUserEmails?: string[];
    hasError: boolean;
    setHasError: (hasError: boolean) => void;
    /** When provided, use these instead of BillingController */
    availableSeatCountOverride?: number;
    paidSeatCountOverride?: number;
    dataTestIdPrefix?: string;
    /** "invite-only" shows "X/Y seats used → Add N seat(s)" format; "default" shows regular seats info */
    seatsInfoVariant?: SeatsInfoVariant;
    /** Emails that cannot be removed from the list (e.g. workspace owner) */
    nonDeletableEmails?: string[];
    /** Override existingUserEmailsCount for seats display when emails list already includes those users */
    existingUserEmailsCountOverride?: number;
}

export const validateEmails = (
    emails = [],
    existingUserEmails = [],
    nonDeletableEmails: string[] = []
) => {
    const nonDeletableSet = new Set(
        nonDeletableEmails.map(e => e?.toLowerCase()).filter(Boolean)
    );

    const normalizedEmails = (emails ?? [])
        .map(e => String(e).toLowerCase().trim())
        .filter(Boolean);

    // User's own (non-deletable) email: keep only first occurrence so duplicate is auto-removed on Enter
    const seenNonDeletable = new Set<string>();
    const emailsWithoutDuplicateSelf = normalizedEmails.filter(email => {
        if (nonDeletableSet.has(email)) {
            if (seenNonDeletable.has(email)) return false;
            seenNonDeletable.add(email);
        }
        return true;
    });

    const invalids = emailsWithoutDuplicateSelf.filter(
        email => !nonDeletableSet.has(email) && !emailRegex.test(email)
    );

    // Count occurrences: typed list + existing workspace members (for "already in workspace" duplicate)
    const occurrenceCountByEmail = new Map<string, number>();
    for (const email of emailsWithoutDuplicateSelf) {
        occurrenceCountByEmail.set(
            email,
            (occurrenceCountByEmail.get(email) ?? 0) + 1
        );
    }
    for (const email of new Set(existingUserEmails)) {
        const key = String(email).toLowerCase();
        if (nonDeletableSet.has(key)) continue;
        occurrenceCountByEmail.set(key, (occurrenceCountByEmail.get(key) ?? 0) + 1);
    }

    // Only flag as duplicate if count > 1 and not the user's own email (user must fix distinct duplicates)
    const duplicateSet = new Set<string>();
    occurrenceCountByEmail.forEach((count, email) => {
        if (count > 1 && !nonDeletableSet.has(email)) duplicateSet.add(email);
    });

    const validationError = invalids.length > 0 || duplicateSet.size > 0;

    return {
        validationError,
        normalizedEmails: emailsWithoutDuplicateSelf,
        invalids,
        duplicateSet
    };
};

export const InviteSection: React.FC<InviteSectionProps> = ({ emails, onEmailsChange, nextBillDate, additionalSeatCount, totalDueToday, nextInvoiceTotal, isCalculating, hasError, setHasError, existingUserEmails = [], availableSeatCountOverride, paidSeatCountOverride, dataTestIdPrefix: dataTestIdPrefixProp, seatsInfoVariant = "default", nonDeletableEmails = [], existingUserEmailsCountOverride }) => {
    const [isFocused, setIsFocused] = useState(false);
    const { normalizedEmails, invalids, duplicateSet } = useMemo(
        () => validateEmails(emails, existingUserEmails, nonDeletableEmails),
        [emails, existingUserEmails, nonDeletableEmails]
    );

    const nonDeletableInEmailsCount = useMemo(
        () => emails.filter(e => nonDeletableEmails.some(nde => nde?.toLowerCase() === e?.toLowerCase())).length,
        [emails, nonDeletableEmails]
    );
    const paidSeatCount = paidSeatCountOverride ?? BillingController.paidSeatCount;
    const availableSeatCount =
        (availableSeatCountOverride ?? BillingController.availableSeatCount) -
        (emails.length - nonDeletableInEmailsCount);

    return (
        <>
            <FlexBox gap={20} column left {...(dataTestIdPrefixProp && { "data-testid": `${dataTestIdPrefixProp}-members-input` })}>
                <StyledEmailTagsInput
                    className="tags-input"
                    addKeys={TAG_DELIMITERS}
                    addOnBlur
                    onChange={(emailTags: string[]) => {
                        const validation = validateEmails(emailTags, existingUserEmails, nonDeletableEmails);
                        setHasError(validation.validationError);
                        onEmailsChange(validation.normalizedEmails);
                    }}
                    hasError={hasError}
                    isFocused={isFocused}
                    value={emails}
                    renderTag={props => {
                        const value = String(props.tag).toLowerCase();
                        const isNonDeletable = nonDeletableEmails.some(e => e?.toLowerCase() === value);
                        const isValid = isNonDeletable || (emailRegex.test(value) && !duplicateSet.has(value));
                        return (
                            <StyledEmailChip
                                key={props.key}
                                isValid={isValid}
                                label={value}
                                {...(isNonDeletable
                                    ? {}
                                    : {
                                        onDelete: () => props.onRemove(props.key),
                                        deleteIcon: <CloseIcon style={{ width: 12, height: 12 }} />
                                    })}
                            />
                        );
                    }}
                    renderInput={(inputProps: any) => {
                        const { value, onChange, onKeyDown, onBlur, ref } = inputProps;
                        return (
                            <EmailInput
                                ref={ref}
                                data-testid={dataTestIdPrefixProp ? `${dataTestIdPrefixProp}-members-input-field` : undefined}
                                value={typeof value === "string" ? value : ""}
                                onChange={onChange}
                                onKeyDown={onKeyDown}
                                onBlur={e => {
                                    setIsFocused(false);
                                    onBlur(e);
                                }}
                                onFocus={() => setIsFocused(true)}
                                onPaste={(event: React.ClipboardEvent) => {
                                    event.persist();
                                    const text = event.clipboardData?.getData("text") || "";
                                    if (text) {
                                        event.stopPropagation();
                                        event.preventDefault();
                                        const pasted = text.split(delimiterRegex)
                                            .map(d => d.trim().toLowerCase())
                                            .filter(Boolean);
                                        if (pasted.length) {
                                            const emails = [...normalizedEmails, ...pasted];
                                            const validation = validateEmails(emails, existingUserEmails, nonDeletableEmails);
                                            setHasError(validation.validationError);
                                            onEmailsChange(validation.normalizedEmails);
                                        }
                                    }
                                }}
                                placeholder={emails.length > 0 ? "" : t("Type or paste emails...")}
                            />
                        );
                    }}
                    renderLayout={(tagComponents, inputComponent) => (
                        <EmailTagLayout>
                            {tagComponents}
                            {inputComponent}
                        </EmailTagLayout>
                    )}
                />
                {hasError && (
                    <div style={{ color: "red", fontSize: 12 }}>
                        {invalids.length > 0 && (
                            <>
                                {invalids.length > 1 ? t("Invalid emails") : t("Invalid email")}: {invalids.join(", ")}
                            </>
                        )}
                        {invalids.length > 0 && duplicateSet.size > 0 && "; "}
                        {duplicateSet.size > 0 && (
                            <>
                                {duplicateSet.size > 1 ? t("Duplicate emails") : t("Duplicate email")}: {Array.from(duplicateSet).join(", ")}
                            </>
                        )}
                    </div>
                )}
                <SeatsInfoContent
                    show={!hasError}
                    variant={seatsInfoVariant}
                    availableSeatCount={availableSeatCount}
                    additionalSeatCount={additionalSeatCount ?? 0}
                    emailsCount={emails.length}
                    paidSeatCount={paidSeatCount}
                    existingUserEmailsCount={existingUserEmailsCountOverride ?? existingUserEmails.length}
                    dataTestIdPrefix={dataTestIdPrefixProp}
                />
            </FlexBox>
            {!hasError && (additionalSeatCount > 0) && (
                <div style={{ position: "relative" }}>
                    <FetchingClickShield visible={!!isCalculating} backgroundColor="white" />
                    <Divider color="#EEE" margin={10} />
                    <FlexBox left between fillWidth>
                        <TotalDueToday>{t("Total due today")}</TotalDueToday>
                        <TotalDueToday>{`$${totalDueToday}`}</TotalDueToday>
                    </FlexBox>
                    <Gap10 />
                    <FlexBox left column fillWidth>
                        <SectionContent>
                            {t("Your next bill of {{amount}} for {{seatCount}} total seats will be due on {{date}}.", {
                                amount: `$${nextInvoiceTotal}`,
                                seatCount: additionalSeatCount + paidSeatCount,
                                date: nextBillDate
                            })}
                        </SectionContent>
                    </FlexBox>
                </div>
            )}
        </>
    );
};
